import React, { useContext, useMemo } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { useUnmount } from 'ahooks';
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import doubleArrowImg from '@/assets/image/double-arrow.png';
import { selectedCurrentCustomer } from '@/store/customer';
import { PART_LIST } from '@/pages/ai-consultation/components/select-part';
import { storagePrefixV2 } from '@/constant';
import { BodyChange3DContext, DIMENTION_2_TEXT, MODEL_CARD_CONFIG } from '../utils';
import CustomMarquee from '../custom-marquee';
import { resetWeightLossStore, setShowType, useWeightLossStore } from './useWeightLossStore';
import ShowWaist from './show-waist';
import ShowBack from './show-back';
import ShowLeftThigh from './show-left-thigh';
import ShowRightThigh from './show-right-thigh';
import ShowLeftCalf from './show-left-calf';
import ShowRightCalf from './show-right-calf';
import ShowLeftArm from './show-left-arm';
import ShowRightArm from './show-right-arm';

import cx from './index.module.scss';

interface Props {}

const SHOWTYPE_2_COMPONENT: Record<keyof GetLastMeasurementResponse, JSX.Element> = {
  backThickness: <ShowBack />,
  waistCircumference: <ShowWaist />,
  leftThighCircumference: <ShowLeftThigh />,
  rightThighCircumference: <ShowRightThigh />,
  leftCalfCircumference: <ShowLeftCalf />,
  rightCalfCircumference: <ShowRightCalf />,
  leftArmCircumference: <ShowLeftArm />,
  rightArmCircumference: <ShowRightArm />,
};

const WeightLoss: React.FC<Props> = () => {
  const showType = useWeightLossStore.use.showType();
  const currentCustomer = useSelector(selectedCurrentCustomer);
  const { bodyChangeInfo, weightLossDescText } = useContext(BodyChange3DContext);

  useUnmount(() => {
    resetWeightLossStore();
  });

  const formatModelCardConfig: Array<{
    initValue: number;
    currentValue: number;
    name: keyof GetLastMeasurementResponse;
    [key: string]: any;
  }> = useMemo(
    () =>
      MODEL_CARD_CONFIG?.map(item => ({
        ...item,
        initValue: bodyChangeInfo?.weightLossList?.find(item2 => item2.problem === item?.name)?.initValue,
        currentValue: bodyChangeInfo?.weightLossList?.find(item2 => item2.problem === item?.name)?.currentValue,
      })),
    [bodyChangeInfo?.weightLossList]
  );

  return (
    <div className={cx('WeightLoss')}>
      {showType === 'all' && (
        <CustomMarquee
          content={
            <>
              {!!bodyChangeInfo?.weightLossHopes?.length && (
                <>
                  您的诉求是{' '}
                  <span className="highlight">
                    {bodyChangeInfo?.weightLossHopes
                      ?.map(item => PART_LIST?.find(item2 => item2.part === item)?.name)
                      ?.join('，')}
                  </span>
                  ，
                </>
              )}
              已内源热能{bodyChangeInfo?.radioFrequencyTreatmentCount}个疗程，{weightLossDescText}
            </>
          }
        />
      )}
      <div className={cx('main-area')}>
        <AnimatePresence mode="wait">
          {showType === 'all' ? (
            <motion.div key={showType} className={cx('all-part')}>
              <motion.div className={cx('mirror-bg')} exit={{ display: 'none', transition: { duration: 0 } }} />
              <motion.div
                className={cx('img-box')}
                transition={{ duration: 0.8 }}
                initial={{ top: '30px', opacity: 0.2 }}
                animate={{ top: '0', opacity: 1 }}
                exit={{ left: '-150px', opacity: 0.1, transition: { duration: 0.4 } }}
              >
                <img
                  src={
                    currentCustomer?.gender === 'Female'
                      ? storagePrefixV2 + '/3d-body-compare/body-3d-female-2.png'
                      : storagePrefixV2 + '/ai-consulation/male-model-bg/asfqfwqfwqf.png'
                  }
                  alt=""
                />
              </motion.div>
              {formatModelCardConfig.map((item, index) => (
                <motion.div
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.05 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  key={item.name}
                  exit={{ display: 'none', transition: { duration: 0 } }}
                  className={cx('card-item')}
                  onClick={() => setShowType(item.name as any)}
                >
                  <div className={cx('dot')} style={item.dotConfig}>
                    <div
                      className={cx(
                        'inner-circle',
                        item.initValue && item.currentValue
                          ? item.initValue !== item.currentValue
                            ? 'green'
                            : 'blue'
                          : 'gray'
                      )}
                    />
                  </div>
                  <div className={cx('connection-line')} style={item.connectionLineConfig} />
                  <div className={cx('value-content')} style={item.valueContentConfig}>
                    <div className={cx('value-header')}>
                      <div className={cx('header-left')}>
                        <div>{DIMENTION_2_TEXT[item.name]}</div>
                        {!!(item.initValue && item.currentValue && item.initValue !== item.currentValue) && (
                          <div className={cx('tag')}>{(item.currentValue - item.initValue) / 10}厘米</div>
                        )}
                      </div>
                      <Icon name="arrow-right" />
                    </div>
                    <div className={cx('value-footer')}>
                      <div className={cx('footer-left')}>
                        {!!item.initValue ? (
                          <>
                            <span>最高</span>
                            <span className={cx('highlight')}>{item?.initValue / 10}</span>
                          </>
                        ) : (
                          <span className={cx('empty')}>暂无</span>
                        )}
                      </div>
                      <div className={cx('footer-middle')}>
                        <img src={doubleArrowImg} alt="" />
                      </div>
                      <div className={cx('footer-right')}>
                        {!!item.currentValue ? (
                          <>
                            <span>目前</span>
                            <span className={cx('highlight', 'green')}>{item?.currentValue / 10}</span>
                            <span>厘米</span>
                          </>
                        ) : (
                          <span className={cx('empty')}>暂无</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <React.Fragment key={showType}>{SHOWTYPE_2_COMPONENT[showType]}</React.Fragment>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default WeightLoss;
